import { useState, useEffect, useMemo } from 'react';
import ItemsManagement from '@/components/items/ItemsManagement';
import { useMaintenanceProjectsQuery } from '@/hooks/maintenance/useMaintenanceProjectsQuery';
import { useOptimizedMaintenanceQuery } from '@/hooks/maintenance/useOptimizedMaintenanceQuery';
import { logger } from '@/lib/logger';
import { globalFeatureFlags, useGlobalFeatureFlag } from '@/lib/featureFlags';

import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import { useServiceTeamMembers } from '@/hooks/maintenance/useServiceTeamMembers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { User, X, RefreshCw, ChevronsUpDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/**
 * Service Items View - Shows items filtered for projects with status='service'
 * Uses optimized query to fetch only service projects from database
 * Phase 2 Week 5.2: Smart pagination support
 * Feature flag: ENABLE_SERVICE_ITEM_ASSIGNMENT for assignee filter
 */
// Normalize Vietnamese diacritics for search matching
const normVi = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

const ServiceItemsView = () => {
  const isOptimizedQueryEnabled = useGlobalFeatureFlag('ENABLE_MAINTENANCE_SMART_PAGINATION');
  const { getStandardValue, setStandardValue } = useLocalStorageManager();
  
  // Item type filter state with localStorage persistence
  const [itemTypeFilter, setItemTypeFilter] = useState<'service' | 'build' | 'all'>(() => {
    return getStandardValue('service-items-type-filter', 'service', 'SETTINGS') as 'service' | 'build' | 'all';
  });

  // ✅ Assignee filter state (for service projects only)
  const [assigneeFilter, setAssigneeFilter] = useState<string[]>([]);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  // Persist to localStorage on change
  useEffect(() => {
    setStandardValue('service-items-type-filter', itemTypeFilter, 'SETTINGS');
    logger.debug('[ServiceItemsView] Item type filter changed:', itemTypeFilter);
  }, [itemTypeFilter, setStandardValue]);

  const handleFilterChange = (value: string) => {
    setItemTypeFilter(value as 'service' | 'build' | 'all');
  };

  // Use optimized or legacy query based on feature flag
  const legacyQuery = useMaintenanceProjectsQuery(
    { 
      searchQuery: '', 
      projectGroup: '', 
      projectType: [], 
      teamLead: '', 
      company: '' 
    },
    1,
    100 // Large page size to get all service projects in one request
  );

  const optimizedQuery = useOptimizedMaintenanceQuery(
    {
      searchQuery: '',
      projectGroup: '',
      projectType: [],
      teamLead: '',
      company: ''
    },
    1,
    50 // Reduced page size with smart pagination
  );

  const { data: serviceData, isLoading, error, refetch } = isOptimizedQueryEnabled 
    ? optimizedQuery 
    : legacyQuery;


  const serviceProjectIds = useMemo(() => {
    const ids = serviceData?.projects?.map(p => p.id) || [];
    
    // 🐛 DEBUG: Enhanced logging for service projects (DEV only)
    if (import.meta.env.DEV) {
      console.log('[ServiceItemsView] DEBUG:', { 
        serviceProjectCount: ids.length,
        serviceProjectIds: ids,
        itemTypeFilter,
        assigneeFilter,
        isItemTypeEnabled: globalFeatureFlags.isEnabled('ENABLE_ITEM_TYPE_FILTERING'),
        isAssigneeEnabled: true, // ✅ HARDCODED (PR-F1)
        rawServiceData: serviceData?.projects
      });
    }
    
    logger.debug('[ServiceItemsView] Service project IDs:', { 
      count: ids.length,
      ids 
    });
    
    return ids;
  }, [serviceData, itemTypeFilter, assigneeFilter]);

  // ✅ HARDCODED (PR-F1 2026-04-30): ENABLE_SERVICE_ITEM_ASSIGNMENT retired, always enabled
  
  // Team members = mọi người có can_view trên ÍT NHẤT 1 dự án service (gom tất cả, không lấy dự án đầu)
  const { data: teamMembers = [] } = useServiceTeamMembers(serviceProjectIds);

  const handleAssigneeSelect = (userId: string) => {
    if (userId && !assigneeFilter.includes(userId)) {
      setAssigneeFilter([...assigneeFilter, userId]);
    }
  };

  const handleRemoveAssignee = (userId: string) => {
    setAssigneeFilter(assigneeFilter.filter(id => id !== userId));
  };

  const clearAssigneeFilter = () => {
    setAssigneeFilter([]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading service items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    logger.error('[ServiceItemsView] Error loading service projects:', error);
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-destructive">Lỗi tải dữ liệu dự án bảo trì</p>
          <p className="text-sm text-muted-foreground mt-1">
            {error instanceof Error ? error.message : 'Đã xảy ra lỗi không xác định'}
          </p>
          <Button onClick={() => refetch()} variant="outline" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Thử lại
          </Button>
        </div>
      </div>
    );
  }

  if (serviceProjectIds.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-muted-foreground">No service projects available</p>
        </div>
      </div>
    );
  }

  // ✅ Feature flag check for item_type filtering
  const isItemTypeEnabled = globalFeatureFlags.isEnabled('ENABLE_ITEM_TYPE_FILTERING');

  // Pass restrictedProjectIds to ensure only service project items are shown
  return (
    <div className="service-items-view space-y-4">
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Item Type Filter Toggle */}
        {isItemTypeEnabled && (
          <Select value={itemTypeFilter} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-[170px] h-9">
              <SelectValue placeholder="Loại việc" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="service">Loại: Service (svc)</SelectItem>
              <SelectItem value="build">Loại: Build</SelectItem>
              <SelectItem value="all">Tất cả loại</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* ✅ Assignee Filter - always enabled (PR-F1) */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 border-l pl-3">
            <User className="h-4 w-4 text-muted-foreground shrink-0" />
            <Popover open={assigneeOpen} onOpenChange={(open) => { setAssigneeOpen(open); if (!open) setAssigneeSearch(''); }}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={assigneeOpen}
                  aria-label="Người phụ trách"
                  className="w-[160px] h-9 justify-between font-normal"
                >
                  <span className="text-muted-foreground">Chọn người...</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Tìm kiếm..."
                    value={assigneeSearch}
                    onValueChange={setAssigneeSearch}
                  />
                  <CommandList>
                    <CommandEmpty>Không tìm thấy</CommandEmpty>
                    <CommandGroup>
                      {teamMembers
                        .filter((member) => normVi(member.full_name).includes(normVi(assigneeSearch)))
                        .map((member) => {
                          const picked = assigneeFilter.includes(member.id);
                          return (
                            <CommandItem
                              key={member.id}
                              value={member.id}
                              onSelect={() => {
                                handleAssigneeSelect(member.id);
                                setAssigneeOpen(false);
                              }}
                            >
                              <Avatar className="mr-2 h-6 w-6">
                                <AvatarImage src={member.avatar_url || undefined} />
                                <AvatarFallback className="text-xs">
                                  {member.full_name?.charAt(0)?.toUpperCase() || '?'}
                                </AvatarFallback>
                              </Avatar>
                              {member.full_name}
                              {picked && <Check className="ml-auto h-4 w-4" />}
                            </CommandItem>
                          );
                        })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Selected assignees as badges */}
          {assigneeFilter.length > 0 && (
            <div className="flex flex-wrap gap-1 pl-3">
              {assigneeFilter.map((userId) => {
                const member = teamMembers.find(m => m.id === userId);
                return (
                  <Badge
                    key={userId}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {member?.full_name || 'Unknown'}
                    <button
                      type="button"
                      onClick={() => handleRemoveAssignee(userId)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAssigneeFilter}
                className="h-6 px-2 text-xs"
              >
                Xóa bộ lọc
              </Button>
            </div>
          )}
        </div>
      </div>
      
      <ItemsManagement 
        hideStatusFilter={true}
        restrictedProjectIds={serviceProjectIds}
        itemTypeFilter={isItemTypeEnabled 
          ? (itemTypeFilter === 'all' ? undefined : itemTypeFilter)
          : undefined
        }
        assigneeFilter={assigneeFilter.length > 0 
          ? assigneeFilter 
          : undefined
        }
        showItemTypeBadges={true}
        hideSearchInput={true}
      />
    </div>
  );
};

export default ServiceItemsView;
